import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { Role } from '@/constants/roles';
import { ROUTES } from '@/constants/routes';
import { apolloClient } from '@/graphql/client';
import { authService } from '@/services/authService';
import type { AuthState, CurrentUser, LoginResult } from '@/types/auth.types';
import { toNameCase } from '@/utils/formatters';

import { onForceLogout } from './authEvents';
import { sessionChannel } from './sessionChannel';
import { clearTokens, setTokens } from './tokenStore';

/**
 * Normalizes the session user's display name to Title Case at the ONE session boundary, so every
 * surface that reads `user.firstName`/`user.lastName` (top bar, dashboard greeting, profile card,
 * avatar initials) shows a consistent "Firstname Lastname" however it was typed at registration.
 * Presentation-only — the stored value on the server is untouched; nothing writes this back.
 */
function normalizeSessionUser(user: CurrentUser): CurrentUser {
  return { ...user, firstName: toNameCase(user.firstName), lastName: toNameCase(user.lastName) };
}

/** Redirect the browser to the "session ended" screen, telling it why (expired vs. logged-out
 * elsewhere) so it shows the right copy. A hard navigation guarantees a full teardown — the user is
 * never left rendering an authenticated page. */
function redirectToSessionEnd(reason: 'expired' | 'ended'): void {
  const target = `${ROUTES.sessionExpired}?reason=${reason}`;
  if (!window.location.pathname.startsWith(ROUTES.sessionExpired)) {
    window.location.assign(target);
  }
}

export interface AuthContextValue extends AuthState {
  /** Resolves to the login result — check `mfaRequired` before assuming the session started. */
  login: (input: { email: string; password: string; mfaCode?: string }) => Promise<LoginResult>;
  /**
   * Same session-establishing behavior as `login`, for the Google OAuth token-exchange flow. Pass
   * `mfaCode` on the follow-up call when `mfaRequired` came back true (BR-2). Pass `termsAccepted`
   * only on a retry after the backend refused a first-time registration with `Legal.AcceptanceRequired`
   * (ADR-0026) — an existing account is never asked to accept again.
   */
  loginWithGoogle: (accessToken: string, mfaCode?: string, termsAccepted?: boolean) => Promise<LoginResult>;
  logout: () => Promise<void>;
  /**
   * Re-runs the silent refresh to pick up server-side changes to the CURRENT session — e.g. the
   * `Candidate` role granted by `becomeCandidate`. The roles live in the access token, so without
   * this the new persona stays invisible until the next hard reload.
   */
  refreshSession: () => Promise<void>;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: readonly Role[]) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const initialState: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  // Start in a loading state and attempt a silent restore on mount (the effect below): the backend
  // holds the refresh token in an `HttpOnly` cookie (ADR-0006), so a hard reload can transparently
  // re-establish the session even though the in-memory access token was lost.
  isLoading: true,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  // Latest auth flag readable from event handlers (BroadcastChannel) without stale closures.
  const isAuthenticatedRef = useRef(state.isAuthenticated);
  useEffect(() => {
    isAuthenticatedRef.current = state.isAuthenticated;
  }, [state.isAuthenticated]);

  const applyLoginResult = useCallback((result: LoginResult) => {
    if (result.tokens) {
      setTokens(result.tokens.accessToken);
      setState({
        user: normalizeSessionUser(result.tokens.user),
        accessToken: result.tokens.accessToken,
        isAuthenticated: true,
        isLoading: false,
      });
      // Tell peer tabs a session just started so they can adopt it (they share the refresh cookie).
      sessionChannel.post('login');
    }
  }, []);

  const login = useCallback<AuthContextValue['login']>(
    async (input) => {
      const result = await authService.login(input);
      applyLoginResult(result);
      return result;
    },
    [applyLoginResult],
  );

  const loginWithGoogle = useCallback<AuthContextValue['loginWithGoogle']>(
    async (accessToken, mfaCode, termsAccepted) => {
      const result = await authService.loginWithGoogle(accessToken, mfaCode, termsAccepted);
      applyLoginResult(result);
      return result;
    },
    [applyLoginResult],
  );

  const logout = useCallback(async () => {
    try {
      // The refresh token is read from the `HttpOnly` cookie server-side; nothing to pass here.
      await authService.logout();
    } catch {
      // A failed logout call (e.g. already-expired access token) must not strand the user in the
      // app — fall through and clear the local session regardless.
    } finally {
      clearTokens();
      setState({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
      // clearStore(), not resetStore() — resetStore refires active queries against a
      // now-unauthenticated client (docs/development/FrontendGuidelines.md §5.5).
      await apolloClient.clearStore();
      // Tear the session down in every other open tab too (Task 3 cross-tab logout).
      sessionChannel.post('logout');
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const tokens = await authService.refresh();
    setTokens(tokens.accessToken);
    setState({
      user: normalizeSessionUser(tokens.user),
      accessToken: tokens.accessToken,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  // Silent session restore on mount (ADR-0006): try to exchange the `HttpOnly` refresh cookie for a
  // fresh access token. Success re-establishes the session across a hard reload; failure (no/expired
  // cookie) simply resolves to the unauthenticated state — never a force-logout redirect on first load.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const tokens = await authService.refresh();
        if (!active) return;
        setTokens(tokens.accessToken);
        setState({
          user: normalizeSessionUser(tokens.user),
          accessToken: tokens.accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        if (!active) return;
        clearTokens();
        setState({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Token refresh failed (errorLink/httpClient) — the session cannot continue (Task 4). Clear auth +
  // cache, tell peer tabs to log out too, and hard-redirect to the "session expired" screen so the
  // user is never left rendering an authenticated page.
  useEffect(
    () =>
      onForceLogout(() => {
        clearTokens();
        setState({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
        void apolloClient.clearStore();
        sessionChannel.post('logout');
        redirectToSessionEnd('expired');
      }),
    [],
  );

  // Cross-tab session sync (Task 3). React to lifecycle events posted by other tabs:
  //  • logout — a peer signed out (or their refresh failed): tear down here and show "session ended".
  //  • login  — a peer signed in: adopt the now-shared session via a silent refresh if we have none.
  useEffect(
    () =>
      sessionChannel.subscribe((event) => {
        if (event.type === 'logout') {
          if (!isAuthenticatedRef.current) return; // already signed out here — nothing to tear down
          clearTokens();
          setState({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
          void apolloClient.clearStore();
          redirectToSessionEnd('ended');
        } else if (event.type === 'login') {
          if (isAuthenticatedRef.current) return; // already signed in here
          void (async () => {
            try {
              const tokens = await authService.refresh();
              setTokens(tokens.accessToken);
              setState({
                user: normalizeSessionUser(tokens.user),
                accessToken: tokens.accessToken,
                isAuthenticated: true,
                isLoading: false,
              });
            } catch {
              // The shared cookie wasn't usable here — stay signed out, no disruption.
            }
          })();
        }
      }),
    [],
  );

  const hasRole = useCallback(
    (role: Role) => Boolean(state.user?.roles.includes(role)),
    [state.user],
  );
  const hasAnyRole = useCallback(
    (roles: readonly Role[]) => Boolean(state.user?.roles.some((r) => roles.includes(r))),
    [state.user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      loginWithGoogle,
      logout,
      refreshSession,
      hasRole,
      hasAnyRole,
    }),
    [state, login, loginWithGoogle, logout, refreshSession, hasRole, hasAnyRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
